import type { ClientItem, LoadedItem } from '../dto/searchResultHit';
import type { ItemCreationRequestDto } from '../dto/itemCreationRequestDto';
import type { HighresDeletion } from '../dto/itemPropTypes';
import type { ExtendedItemDto } from '../dto/extendedItemDto';
import type { itemAnalyticsProperties } from '../dto/itemModelPrimitives';
import { isItem } from '../dto/searchResultHit';
import { ItemState } from '../dto/itemState';
import { ItemType, type SearchItemType } from '../dto/itemTypes';
import type { AnalyticsQueue } from '../dto/asyncOperationModels';
import { AsyncOperationState } from '../dto/asyncOperationPrimitives';
import { isLanguageCode, isTranscriptionService } from '../dto/asyncOperationModels';
import type { AsyncOperationType } from '../dto/asyncOperationType';
import type { EnabledAnalyticsFieldName, StateFieldName } from '../search/interfaces';
import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import type { MediaAvailability } from '../dto/mediaAvailability';
import { StreamRoleId } from '../dto/mediaAvailability';
import { MediaAvailabilityStatus } from '../dto/mediaDtos';
import type { SearchCategory, SearchCategoryAlias } from '../dto/searchModels';
import type { AnalyticConfig } from '../dto/tenantModel';
import { proxyFormats } from '../dto/tenantModel';
import type { TsStream, LiveRecording } from '../dto/liveItemProps';
import { MPEGTS_TIMEBASE } from '../dto/liveDtos';
import { addTc, getEndFrameMillisecondsInclusive, getMsFromTimecodeString, getStartFrameMillisecondsInclusive, getTc, isDropFrameTc } from './timecode';
import { doesItemHaveFlatHighres, doesItemHaveHighresTsSegments, getProxyFileNameAndMimeType, hasMediaSegments, isCurrentlyLive } from '../item/media';
import { OperationConclusion } from '../dto/progress';
import { isArrayOfNumber } from './general';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { CompleteType } from './general';
import type { Logger } from '../types/logger';
import { assert } from './invariant';
import { TechnicalVideoDataField, TechnicalAudioAndVideoDataField, getTechnicalMetadataValue } from './technicalMetadata';
import type { Fraction } from './fractionType';
import { approximateFractionValue, approximateNumber, frac } from './integerMath';
import type { ItemMetaData } from '../dto/fieldValue';
import { isVirtualClip } from '../dto/virtualClipUtil';
import pickBy from 'lodash/pickBy';

/**
 * When showing a video/audio item, the timeline goes by default from
 * 0 to the media duration. For items that do have an embedded start timecode,
 * the timeline uses it as the starting point. For live items with a UTC start
 * timestamp, we use the start point but we display the time indicators as
 * wallclock ones, i.e. wrapping at midnight.
 */
export interface StartPoint {
  offsetInMs: number;
  showAsLocalTime: boolean;
}

/**
 * TODO: We should not store the generated timed transcript on the item object, it contains references to other parts of the item json which
 * causes circular references to be present. To be able to stringify item objects on the client, we need to exclude these which this function
 * takes care of.
 */
export const getLoadedItemJson = (item: LoadedItem | null) => {
  if (item === null) return;
  const clone = cloneDeep(item);
  delete clone.timedTranscript;
  delete clone.timedTranslatedTranscript;
  return JSON.stringify(clone, null, 2);
};


export const getIconForSearchCategory = (type: SearchCategory | undefined) => {
  switch (type) {
    case ItemType.CLIP_LIST: return 'movie';
    case ItemType.TIMELINE: return 'movie';
    case ItemType.VIDEO: return 'videocam';
    case ItemType.IMAGE: return 'image';
    case ItemType.PERSON: return 'person';
    case ItemType.FOLDER: return 'folder';
    case ItemType.AUDIO: return 'audiotrack';
    case MediaAvailabilityStatus.STREAMING: return 'view_stream';
    default: return 'insert_drive_file';
  }
};


export const getIconForSearchCategoryAlias = (type: SearchCategoryAlias | undefined) => {
  switch (type) {
    case 'sequence': return 'movie';
    case ItemType.VIDEO: return 'videocam';
    case ItemType.IMAGE: return 'image';
    case ItemType.PERSON: return 'person';
    case ItemType.FOLDER: return 'folder';
    case ItemType.AUDIO: return 'audiotrack';
    case MediaAvailabilityStatus.STREAMING: return 'view_stream';
    default: return 'insert_drive_file';
  }
};

export function getIconForItem(item: ClientItem) {
  if (item.scheduledRecording && Date.now() < item.scheduledRecording.startTimeMs) return 'schedule';
  if (isVirtualClip(item.virtualClipInfo)) return 'content_cut';
  return item.typeIcon ? item.typeIcon : getIconForSearchCategory(item.itemType);
}


export const getLabelForSearchCategory = (type: SearchCategory) => {
  switch (type) {
    case ItemType.CLIP_LIST:
    case ItemType.TIMELINE: return 'Sequence';
    case MediaAvailabilityStatus.STREAMING: return 'Live';
    default: return capitalize(type);
  }
};

export const getKeyForSearchCategoryAlias = (type: SearchCategoryAlias) => {
  switch (type) {
    case ItemType.VIDEO: return 'ItemTypes.Video';
    case ItemType.IMAGE: return 'ItemTypes.Image';
    case ItemType.PERSON: return 'ItemTypes.Person';
    case ItemType.FOLDER: return 'ItemTypes.Folder';
    case ItemType.AUDIO: return 'ItemTypes.Audio';
    case MediaAvailabilityStatus.STREAMING: return 'ItemTypes.Live';
    case ItemType.FILE: return 'ItemTypes.File';
    case 'sequence': return 'ItemTypes.ClipList';
  }
};

export const getKeyForSearchItemType = (type: SearchItemType) => {
  switch (type) {
    case ItemType.VIDEO: return 'ItemTypes.Video';
    case ItemType.IMAGE: return 'ItemTypes.Image';
    case ItemType.PERSON: return 'ItemTypes.Person';
    case ItemType.FOLDER: return 'ItemTypes.Folder';
    case ItemType.AUDIO: return 'ItemTypes.Audio';
    case ItemType.FILE: return 'ItemTypes.File';
    case ItemType.CLIP_LIST:
    case ItemType.TIMELINE: return 'ItemTypes.ClipList';
  }
};


export const getIsCanceledStateFromItem = (item: LoadedItem, operationType: AsyncOperationType) => {
  switch(operationType) {
    case 'transcoding':
      return typeof item.transcodingJobCanceled === 'boolean' ? item.transcodingJobCanceled : false;
    case 'transcription':
      return typeof item.transcriptionJobCanceled === 'boolean' ? item.transcriptionJobCanceled : false;
    case 'personDetection':
      return typeof item.personDetectionJobCanceled === 'boolean' ? item.personDetectionJobCanceled : false;
    case 'celebrityDetection':
      return typeof item.celebrityDetectionJobCanceled === 'boolean' ? item.celebrityDetectionJobCanceled : false;
    case 'labelDetection':
      return typeof item.labelDetectionJobCanceled === 'boolean' ? item.labelDetectionJobCanceled : false;
    case 'textDetection':
      return typeof item.textDetectionJobCanceled === 'boolean' ? item.textDetectionJobCanceled : false;
    default: return null;
  }
};

const getEnabledAnalyticsFieldName = (operationType: AsyncOperationType): EnabledAnalyticsFieldName | null => {
  switch (operationType) {
    case 'transcription': return 'transcriptionEnabled';
    case 'personDetection': return 'personDetectionEnabled';
    case 'celebrityDetection': return 'celebrityDetectionEnabled';
    case 'labelDetection': return 'labelDetectionEnabled';
    case 'textDetection': return 'textDetectionEnabled';
    default: return null;
  }
};


export const getStateFieldName = (operationType: AsyncOperationType): StateFieldName | null => {
  switch (operationType) {
    case 'transcoding': return  'transcodingState';
    case 'transcription': return 'transcriptionState';
    case 'personDetection': return 'personDetectionState';
    case 'celebrityDetection': return 'celebrityDetectionState';
    case 'labelDetection': return 'labelDetectionState';
    case 'textDetection': return 'textDetectionState';
    case 'archiveFileTransfer': return 'archiveFileTransferState';
    default: return null;
  }
};

/**
 * The order of these also determines the order they are drawn in the Analytics tab.
 */
export const supportedAnalyticsOps = <const>[
  'transcoding',
  'transcription',
  'personDetection',
  'celebrityDetection',
  'labelDetection',
  'textDetection',
  'sceneDetection',
];
export type SupportedAnalytics = typeof supportedAnalyticsOps[number];

export const supportedOps: AsyncOperationType[] = [
  ...supportedAnalyticsOps,
  'archiveFileTransfer'
];

export const getOngoingAnalyticsOps = (item: LoadedItem, opsToCheck: AsyncOperationType[] = supportedOps) => {
  const ongoingOps: AnalyticsQueue[] = [];
  for (const op of opsToCheck) {
    const isCancelled = getIsCanceledStateFromItem(item, op);
    const stateFieldName = getStateFieldName(op);
    const enabledField = getEnabledAnalyticsFieldName(op);
    if (stateFieldName === null) continue;

    // item[stateFieldName] is set to null when the job is canceled. This implies the job should not be on the
    // "ongoing" list anymore.
    const hasStopped = item[stateFieldName] === AsyncOperationState.COMPLETE || item[stateFieldName] === AsyncOperationState.ERROR || item[stateFieldName] === null;

    // We consider not only progressing analytics as ongoing to poll, but also any
    // subsequent ops that are scheduled to run.
    if (item[stateFieldName] === AsyncOperationState.PROGRESSING && !isCancelled) {
      ongoingOps.push({
        op,
        originalState: undefined,
      });
    } else if (enabledField !== null && !isCancelled && !hasStopped && item[enabledField] === true) {
      ongoingOps.push({
        op,
        originalState: undefined,
      });
    }
  }

  if (item.analyticPlugins) {
    for (const plugin of Object.values(item.analyticPlugins)) {
      if (plugin.status === AsyncOperationState.PROGRESSING) {
        ongoingOps.push({
          type: 'plugin',
          pluginId: plugin.id,
        });
      }
    }
  }

  return ongoingOps;
};

const isTranscodingRightNow = (item: Pick<ExtendedItemDto, 'transcodingState'>) => {
  return item.transcodingState === AsyncOperationState.INITIATED || item.transcodingState === AsyncOperationState.PROGRESSING;
};

export const usingEditProxy = (item: Pick<ExtendedItemDto, 'hasEditProxy' | 'editProxyIngested'>) => {
  return item.hasEditProxy || item.editProxyIngested;
};

// checks if we can proceed to download edit-proxy (or web-proxy)
export const isProxyReadyForTransferAttempt = (item: Pick<ExtendedItemDto, 'transcodingState' | 'hasEditProxy' | 'editProxyIngested' | 'editProxyAvailable'>) => {
  // we wait if it is transcoding right now. not if it is in other states (blank/error: we should try and fail and cleanup, complete: we should try and succeed and cleanup).
  const transcodingRightNow = isTranscodingRightNow(item);

  // edit-proxy takes precedence
  const epNeeded = usingEditProxy(item);
  if (epNeeded) {
    const epAvailableForDownloadAttempt =
      item.editProxyAvailable     // newly added, made available sooner before web-proxy is done.
        ||
      !transcodingRightNow;       // for older items that do not have editProxyAvailable set

    return epAvailableForDownloadAttempt;
  } else {
    // web-proxy
    return !transcodingRightNow;
  }
};

export const doesItemOnlyHaveProxy = (item: ClientItem | null) => {
  return !!(isItem(item) && item.proxy && !item.highRes && !item.aaf && !doesItemHaveHighresTsSegments(item));
};


export const isSearchHitProxyOnlyItem = (item: ClientItem | null) => {
  // checking item state here as items in search result never have highres url
  // so, just checking if item object doesn't contain highres will give a false positive
  return doesItemOnlyHaveProxy(item) && item?.itemState === ItemState.NEW;
};

export const isPlaceholderItem = (item: Partial<Pick<LoadedItem, 'itemState' | 'proxy' | 'highRes' | 'media' | 'liveRecording' | 'tamsReference'>> | null): boolean => {
  if (item?.tamsReference) {
    return false;
  }
  return isItem(item) && item.itemState === ItemState.NEW && !item.proxy && !item.highRes && !item.media && !item.liveRecording;
};

/**
 * This function returns the initial playback position in milliseconds for live items for auto playback from a
 * few seconds prior to the current live edge. It returns undefined for other cases, e.g. non-live items.
 */
export const getInitialPlayStartTime = (item: Pick<ClientItem, 'media' | 'liveRecording' | 'isLive' | 'mediaDuration'>): number | undefined => {
  if (!isCurrentlyLive(item)) {
    return undefined;
  }
  const segmentDurationFraction = item?.media?.segmentDuration;
  const segmentCount = item?.media?.segmentCount;
  if (!segmentDurationFraction || !segmentCount) {
    return undefined;
  }

  /**
   * Start from the segment before the last one, convert the time to milliseconds, and start from an offset of 1 second into that second.
   * Without the offset, I have seen the playback not starting for some cases. I think it may be because of some rounding issues.
   */
  const result = (segmentCount - 2) * segmentDurationFraction.numerator * 1000 / segmentDurationFraction.denominator + 1000;
  if (result < 0) {
    return 0;
  }

  return result;
};


export const isTranscribingLive = (item: Pick<ClientItem, 'media' | 'liveRecording' | 'isLive' | 'transcriptionEnabled' | 'transcriptionState'> | null): boolean => {
  const isLive = isCurrentlyLive(item) && item?.transcriptionEnabled && item?.transcriptionState === AsyncOperationState.PROGRESSING;
  return !!isLive;
};

export function createEditProxyFileNameWithExtension(extensionWithDot: string) {
  return `edit_proxy${extensionWithDot}`;
}

/**
 * The purpose of this function is vague. Historically, it has been used both
 * for deciding where to upload the edit proxy, as well as trying to figure out
 * where the edit proxy is when trying to download it.
 *
 * However, when downloading the edit proxy we prefer to use explicitly stored
 * information about which file name was chosen during upload.
 *
 * TODO: Replace this function with
 * getSystemGeneratedMediaEditProxyDownloadKey() and other more specific
 * functions.
 */
export const getEditProxyFileName = (item: Pick<ExtendedItemDto, 'editProxyFormat'>) => {
  return createEditProxyFileNameWithExtension(getEditProxyExtension(item));
};

/**
 * Get the key to use when downloading the existing edit proxy of an item from
 * the system generated media bucket.
 *
 * This function is for obtaining the key to already existing edit proxy files,
 * not to obtain the key to upload a new edit proxy file.
 */
export function getSystemGeneratedMediaEditProxyDownloadKey(logger: Logger, item: Pick<ExtendedItemDto, 'editProxyLocation' | 'editProxyFormat' | 'id' | 'virtualClipInfo'>): string {
  if (item.editProxyLocation?.path) {
    assertIdPathPrefix(logger, 'editProxyLocation', item.editProxyLocation?.path, item.virtualClipInfo?.masterClipId ?? item.id);
    return item.editProxyLocation.path;
  }

  const implictlyDerivedName = createEditProxyFileNameWithExtension(getEditProxyExtension(item));
  return item.id + '/' + implictlyDerivedName;
}

export function getSystemGeneratedMediaWebProxyDownloadKey(logger: Logger, item: Pick<ExtendedItemDto, 'itemType' | 'id' | 'webProxyLocation' | 'virtualClipInfo'>): string {
  if (item.webProxyLocation?.path) {
    assertIdPathPrefix(logger, 'webProxyLocation', item.webProxyLocation?.path, item.virtualClipInfo?.masterClipId ?? item.id);
    return item.webProxyLocation.path;
  }
  const nameAndMimeType = getProxyFileNameAndMimeType(item.itemType);
  return `${item.id}/${nameAndMimeType.fileName}`;
}

function assertIdPathPrefix(logger: Logger, what: string, path: string, itemId: string) {
  if (!path.startsWith(itemId + '/')) {
    const message = 'Unexpected: system S3 bucket item prefix violation for ' + what;
    logger.log({
      level: 'error',
      message,
      itemId,
      path
    });
    assert(false, message);
  }
}

export const getEditProxyExtension = (item: Pick<ExtendedItemDto, 'editProxyFormat'>) => {
  const editProxyFormatDetails = item.editProxyFormat ? proxyFormats[item.editProxyFormat] : undefined;
  return editProxyFormatDetails?.extension ?? '.mp4';
};


export const getEditProxyContentType = (item: Pick<ExtendedItemDto, 'editProxyFormat'>) => {
  const editProxyFormatDetails = item.editProxyFormat ? proxyFormats[item.editProxyFormat] : undefined;
  return editProxyFormatDetails?.contentType ?? 'video/mp4';
};


export const getLiveItemUtcStartTimeMs = (item: Pick<ExtendedItemDto, 'media'>): number | undefined => {
  // streamStartTimestamp is set when the first segment arrives. This should only be used as
  // the record start point of the stream for items with a highres segmented stream.
  if (item.media?.codecsByStreamId[StreamRoleId.LiveRecordHighresTs] === undefined) {
    return undefined;
  }

  if(item.media?.streamStartTimestamp) {
    return new Date(item.media.streamStartTimestamp).getTime();
  }
  return undefined;
};

export const isDownloadable = (item: ClientItem | LoadedItem) => item.itemType !== ItemType.FOLDER && item.itemType !== ItemType.PERSON;

export function shortestEnabledStream(liveRecording: LiveRecording): TsStream | undefined {
  return [
    liveRecording.highresData,
    liveRecording.webproxyData,
  ].filter((s) => !!s)
  .sort((a, b) => a!.segmentCount - b!.segmentCount)[0];
}

export function getMediaDurationMsOrNull(item: Pick<ClientItem, 'media' | 'technicalMetadata'> & { mediaDuration?: number }): number | null {
  const duration = getMediaDurationOrNull(item);
  if (duration === null) {
    return null;
  }
  return approximateFractionValue(duration) * 1000.0;
}

export function getMediaDurationMSFromMetadata(item: Pick<ClientItem, 'technicalMetadata'> & { mediaDuration?: number }): number {
  const fraction = getMediaDurationFromMetadata(item);
  return fraction ? approximateFractionValue(fraction) * 1000.0 : 0;
}

export function getMediaDurationFromMetadata(item: Pick<ClientItem, 'technicalMetadata'> & { mediaDuration?: number }): Fraction | null {
  const technicalDuration = getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalAudioAndVideoDataField.Duration);
  if (technicalDuration) {
    return approximateNumber(technicalDuration / 1000, 4);
  }

  if (item.mediaDuration) {
    return approximateNumber(item.mediaDuration / 1000, 4);
  }
  return null;
}

export function getMediaDurationOrNull(item: Pick<ClientItem, 'media' | 'technicalMetadata'> & { mediaDuration?: number }): Fraction | null {
  return getMediaDurationFromMetadata(item) ?? getLiveMediaDuration(item);
}

/** @deprecated Use {@link getSourceMediaDuration} or {@link getEffectiveMediaDuration} instead. */
export function getMediaDuration(item: Pick<ClientItem, 'media' | 'technicalMetadata'> & { mediaDuration?: number }): number {
  return getMediaDurationMsOrNull(item) ?? 0;
}

/**
 * Returns the physical media duration in milliseconds, ignoring virtual clip boundaries.
 * Use this when you need the actual underlying media length (e.g. for timeline rendering,
 * transcoding, or segment calculations).
 */
export function getSourceMediaDuration(item: Pick<ClientItem, 'media' | 'technicalMetadata'> & { mediaDuration?: number }): number {
  return getMediaDurationMsOrNull(item) ?? 0;
}

/**
 * Returns the effective duration in milliseconds, accounting for virtual clip boundaries.
 * For virtual clips, returns the subrange duration (outPoint - inPoint).
 * For regular items, returns the physical media duration.
 * Use this when displaying duration to users or for playback calculations.
 */
export function getEffectiveMediaDuration(item: Pick<ClientItem, 'media' | 'technicalMetadata' | 'virtualClipInfo'> & { mediaDuration?: number }): number {
  const vci = item.virtualClipInfo;
  if (isVirtualClip(vci)) {
    return vci.outPoint - vci.inPoint;
  }
  return getMediaDurationMsOrNull(item) ?? 0;
}

export function getMediaDurationInSecondsFromSegmentInfo(media: MediaAvailability): Fraction {
  return frac(media.segmentCount * media.segmentDuration.numerator, media.segmentDuration.denominator);
}

export function getLiveMediaDuration(item: Pick<ClientItem, 'media' | 'liveRecording'>): Fraction | null {
  if (item.liveRecording) {
    const liveRecDur = getCloudLiveMediaDuration(item.liveRecording);
    if (liveRecDur !== null) return liveRecDur;
  }
  if (item.media) return getMediaDurationInSecondsFromSegmentInfo(item.media);
  return null;
}

export function getCloudLiveMediaDurationMs(liveRecording: LiveRecording): number {
  const duration = getCloudLiveMediaDuration(liveRecording);
  if (duration === null) {
    return 0.0;
  }
  return approximateFractionValue(duration) * 1000.0;
}

export function getCloudLiveMediaDuration(liveRecording: LiveRecording): Fraction | null {
  /**
   * FIXME: Move away from deriving duration from item.liveRecording,
   * instead we should be able to derive the duration from item.media
   *
   */
  const stream = shortestEnabledStream(liveRecording);
  if (stream === undefined) {
    return null;
  }
  return calculateCloudLiveSegmentsDuration(stream);
}

function calculateCloudLiveSegmentsDuration(stream: TsStream): Fraction {
  return frac((stream.lastSegmentLastFramePts - stream.firstSegmentFirstFramePts), MPEGTS_TIMEBASE);
}

export type ItemAnalyticProperties = Pick<
  ItemCreationRequestDto,
  typeof itemAnalyticsProperties[number]
 >;

export const convertAnalyticConfig = (cfg: AnalyticConfig): CompleteType<ItemAnalyticProperties> => {
  return {
    transcriptionEnabled: cfg.transcriptionEnabled,
    personDetectionEnabled: cfg.personDetectionEnabled,
    createUnknownPersons: cfg.personDetectionConfig?.createUnknownPersons ?? false,
    celebrityDetectionEnabled: cfg.celebrityDetectionEnabled,
    labelDetectionEnabled: cfg.labelDetectionEnabled,
    textDetectionEnabled: cfg.textDetectionEnabled,
    textDetectionSettings: cfg.textDetectionSettings,
    transcriptionService: cfg.transcriptionConfig?.transcriptionService,
    languageCode: cfg.transcriptionConfig?.transcriptionSourceLanguage,
    transcriptionChannels: cfg.transcriptionConfig?.transcriptionChannels,
    transcriptionChannelIdentification: cfg.transcriptionConfig?.transcriptionChannelIdentification,
    analyticPlugins: cfg.analyticPlugins,
  };
};

export const convertPartialAnalyticConfig = (cfg: Partial<AnalyticConfig>): Partial<ItemAnalyticProperties> => {
  const itemAnalyticProps: CompleteType<Partial<ItemAnalyticProperties>> = {
    transcriptionEnabled: cfg.transcriptionEnabled,
    personDetectionEnabled: cfg.personDetectionEnabled,
    createUnknownPersons: cfg.personDetectionConfig?.createUnknownPersons ?? false,
    celebrityDetectionEnabled: cfg.celebrityDetectionEnabled,
    labelDetectionEnabled: cfg.labelDetectionEnabled,
    textDetectionEnabled: cfg.textDetectionEnabled,
    textDetectionSettings: cfg.textDetectionSettings,
    transcriptionService: cfg.transcriptionConfig?.transcriptionService,
    languageCode: cfg.transcriptionConfig?.transcriptionSourceLanguage,
    transcriptionChannels: cfg.transcriptionConfig?.transcriptionChannels,
    transcriptionChannelIdentification: cfg.transcriptionConfig?.transcriptionChannelIdentification,
    analyticPlugins: cfg.analyticPlugins,
  };
  return pickBy(itemAnalyticProps, val => val != undefined);
};

export function isValidAnalyticConfig(cfg: unknown): cfg is AnalyticConfig {
  if (!isObject(cfg)) return false;

  if (!isBoolean(cfg.transcriptionEnabled)) return false;
  if (!isBoolean(cfg.labelDetectionEnabled)) return false;
  if (!isBoolean(cfg.textDetectionEnabled)) return false;
  if (!isBoolean(cfg.personDetectionEnabled)) return false;
  if (!isBoolean(cfg.celebrityDetectionEnabled)) return false;

  if (cfg.transcriptionConfig !== undefined && !isObject(cfg.transcriptionConfig)) return false;
  if (cfg.transcriptionConfig?.transcriptionService !== undefined && !isTranscriptionService(cfg.transcriptionConfig.transcriptionService)) return false;
  if (cfg.transcriptionConfig?.transcriptionSourceLanguage !== undefined && !isLanguageCode(cfg.transcriptionConfig.transcriptionSourceLanguage)) return false;
  if (cfg.transcriptionConfig?.transcriptionChannels !== undefined && !isArrayOfNumber(cfg.transcriptionConfig.transcriptionChannels)) return false;

  if (cfg.personDetectionConfig !== undefined && !isObject(cfg.personDetectionConfig)) return false;
  if (cfg.personDetectionConfig?.createUnknownPersons !== undefined && !isBoolean(cfg.personDetectionConfig.createUnknownPersons)) return false;

  if (cfg.textDetectionSettings !== undefined && !isObject(cfg.textDetectionSettings)) return false;
  if (cfg.textDetectionSettings?.minWords !== undefined && !isNumber(cfg.textDetectionSettings.minWords)) return false;
  if (cfg.textDetectionSettings?.minHeight !== undefined && !isNumber(cfg.textDetectionSettings.minHeight)) return false;

  return true;
}

export interface ItemWithTimecodeInfo {
  timecode?: string,
  mediaFrameRate?: number,
  technicalMetadata?: ItemMetaData;
}

// Returns a timecode string corresponding to milliseconds into the item, considering embedded timecode of item
function getItemTcString(millisecondsOrTimecode: number | string, item: ItemWithTimecodeInfo, frameRate: number) {
  const dropFrame = isDropFrameTc(item.timecode);
  const tc = getTc(millisecondsOrTimecode, frameRate, dropFrame);
  addTc(tc, item.timecode);
  return tc.toString();
}

export function getStartTcString(milliseconds: number, item: ItemWithTimecodeInfo) {
  const frameRate = getMediaFrameRateOrNull(item);
  if (frameRate === null) return '';
  return getItemTcString(getStartFrameMillisecondsInclusive(milliseconds, frameRate), item, frameRate);
}

export function getEndTcString(milliseconds: number, item: ItemWithTimecodeInfo) {
  const frameRate = getMediaFrameRateOrNull(item);
  if (frameRate === null) return '';
  return getItemTcString(getEndFrameMillisecondsInclusive(milliseconds, frameRate), item, frameRate);
}


/**
 * Given an item, returns the start point to use when rendering
 * time positions in the media.
 */
export function getStartPoint(logger: Logger, item: LoadedItem): StartPoint {
  if (item.timecode) {
    return {
      offsetInMs: getMsFromTimecodeString(logger, item.timecode) ?? 0,
      showAsLocalTime: false,
    };
  }

  if (hasMediaSegments(item)) {
    const startTimeMs = getLiveItemUtcStartTimeMs(item);

    if (startTimeMs !== undefined) {
      return {
        offsetInMs: startTimeMs,
        showAsLocalTime: true,
      };
    }
  }

  return {
    offsetInMs: 0,
    showAsLocalTime: false,
  };
}

/**
 * Returns true if item has media that can be copied to another item.
 * Note that items with segmented/fragmented proxies and flat highres
 * can be copied but the target item will only get the flat proxies /
 * highres.
 */
export const canCopyMediaFromItem = (item: ClientItem) => {
  return !isPlaceholderItem(item) && doesItemHaveFlatHighres(item);
};

export interface ItemIssue {
  level: 'warning' | 'error',
  text: string
}

/**
 * Returns all errors/warnings related to an item in order from most
 * severe to least severe
 */
export function getItemIssues(item: LoadedItem): ItemIssue[] {
  const itemIssues : ItemIssue[] = [];

  if ((item.progressInfo?.conclusion === OperationConclusion.FAILED) && (item.progressInfo.errors !== undefined)) {
    for (const error of item.progressInfo.errors) {
      itemIssues.push({ level: 'error', text: error});
    }
  }

  // Live recording errors override warnings
  if ((item.liveRecording?.state === 'error') && (item.liveRecording?.stateError !== undefined)) {
    itemIssues.push({ level: 'error', text: item.liveRecording?.stateError });
  }

  // Lowest priority are warnings
  if ((item.warnings !== undefined) && (item.warnings.length > 0)) {
    for (const warning of item.warnings) {
      itemIssues.push({ level: 'warning', text: warning});
    }
  }
  return itemIssues
    .filter((issue, index) => index === itemIssues.findIndex((other) => other.text === issue.text));
}

/**
 * For a given analytics operation, returns the properties of an item directly associated with that operation
 */
export const getItemAnalyticsPropertiesForOperation = (
  op: SupportedAnalytics,
  analyticConfig: Pick<AnalyticConfig, 'transcriptionConfig' | 'textDetectionSettings' | 'personDetectionConfig'>
): Partial<ItemAnalyticProperties> => {
  const props: Partial<ItemAnalyticProperties> = {};
  switch (op) {
    case 'transcoding':
      return props;

    case 'transcription':
      props.transcriptionEnabled = true;
      if (analyticConfig.transcriptionConfig) {
        const { transcriptionChannelIdentification, transcriptionChannels, transcriptionService, transcriptionSourceLanguage } = analyticConfig.transcriptionConfig;
        props.transcriptionChannelIdentification = transcriptionChannelIdentification;
        props.transcriptionChannels = transcriptionChannels;
        props.transcriptionService = transcriptionService;
        props.languageCode = transcriptionSourceLanguage;
      }
      return props;

    case 'textDetection':
      props.textDetectionEnabled = true;
      if (analyticConfig.textDetectionSettings) {
        props.textDetectionSettings = analyticConfig.textDetectionSettings;
      }
      return props;

    case 'personDetection':
      props.personDetectionEnabled = true;
      if (analyticConfig.personDetectionConfig) {
        props.createUnknownPersons = analyticConfig.personDetectionConfig.createUnknownPersons;
      }
      return props;

    case 'celebrityDetection':
      props.celebrityDetectionEnabled = true;
      return props;

    case 'labelDetection':
      props.labelDetectionEnabled = true;
      return props;

    case 'sceneDetection':
      // scene detection is being used as an analytics plugin.
      // Nothing to update in here
      return props;
  }
};

export function wasHighresDeleted(item: {
  highresDeletion?: Pick<HighresDeletion, 'deletionState'>
}) {
  return item.highresDeletion?.deletionState === 'processed';
}

export function isHighresDeletionScheduled(item: {
  highresDeletion?: Pick<HighresDeletion, 'deletionState'>
}) {
  return item.highresDeletion?.deletionState === 'scheduled'
    || item.highresDeletion?.deletionState === 'in-progress';
}

export function getMediaFrameRateOrNull(item: Pick<ClientItem, 'technicalMetadata' | 'mediaFrameRate'>): number | null {
  const mediaFrameRate = getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalVideoDataField.FrameRate);
  if (mediaFrameRate) return mediaFrameRate;
  if (item.mediaFrameRate) return item.mediaFrameRate;
  return null;
}

export function getAudioChannelCount(item: { audioChannels?: number, technicalMetadata?: ItemMetaData }): number | null {
  return getTechnicalMetadataValue<number>(item.technicalMetadata, TechnicalAudioAndVideoDataField.AudioChannels) ?? item.audioChannels ?? null;
}

export const INSTIGATOR_SKIP_POST_ACTIONS = 'skip_post_actions-';
